<script setup lang="ts">
import { ref, reactive, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { useQuasar } from 'quasar'
import { storeToRefs } from 'pinia'
import type { Operation } from 'fast-json-patch'
import { useCrudPatch } from '../../composables/useCrudPatch'
import { useModerationStore } from '../../stores/moderation'
import { LMap, LTileLayer, LMarker } from '@maxel01/vue-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { regionConfig } from '@/config/region'
import { dropUnchangedIncidentDateTime } from '@/utils/incident-patch'
import { crashLinkOps } from '@/utils/crash-link-patch'
import { readApiErrorMessage } from '@/utils/api-error'
import type { CrashLinkDecision } from '@/utils/crash-link-patch'

const maxBounds = new L.LatLngBounds(
  [regionConfig.mapBounds.sw.lat, regionConfig.mapBounds.sw.lng],
  [regionConfig.mapBounds.ne.lat, regionConfig.mapBounds.ne.lng]
)
import type {
  AddressDto,
  IncidentReport,
  UserIncidentFootage,
  UserIncidentImage,
  ViewIncidentFileResponse
} from '@roadfootage/api-client'
import { ModerationStatus, MediaProcessingStatus, ResponseError } from '@roadfootage/api-client'
import { QMediaPlayer } from '@quasar/quasar-ui-qmediaplayer'
import DateTimePicker from '../forms/DateTimePicker.vue'
import AddressAutocomplete from '../forms/AddressAutocomplete.vue'
import MediaFileCard from './MediaFileCard.vue'
import CrashLinkPanel from './CrashLinkPanel.vue'
import MediaDownloadDialog from './MediaDownloadDialog.vue'

const moderationStore = useModerationStore()
const $q = useQuasar()

// Use storeToRefs for all reactive state — direct property access loses dependency
// tracking on computed/ref properties in Pinia setup stores
const {
  isDialogOpen,
  activeIncident,
  activeIndex,
  queueTotal,
  queueItems,
  processingByFileId,
  linkedCrash
} = storeToRefs(moderationStore)

const isOpen = computed({
  get: () => isDialogOpen.value,
  set: (val: boolean) => {
    if (!val) moderationStore.closeDialog()
  }
})

const { beginObserving, generatePatch, cleanupObserver } = useCrudPatch()

// --- Form state ---
const mapCenter = ref<[number, number]>([-27.4698, 153.0251])

// l-marker types its icon prop as L.Icon, but a DivIcon works at runtime; cast to satisfy the prop type.
const markerIcon = new L.DivIcon({
  html: `<i class="q-icon mdi mdi-map-marker" style="font-size:32px;color:#e74c3c;filter:drop-shadow(0 1px 3px rgba(0,0,0,0.45))"></i>`,
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  className: ''
}) as unknown as L.Icon

const form = reactive<Partial<IncidentReport>>({
  incidentDescription: '',
  incidentDateTime: undefined,
  address: undefined,
  location: undefined,
  hasContentWarning: undefined,
  isFilmedInPublicLocation: undefined,
  userConsentProvided: undefined
})

// The timestamp as last saved (or loaded). The JSON patch observer mirrors Dates as ISO strings,
// so it cannot tell an unchanged Date from an edited one; buildPatch compares against this instead.
let savedDateTime: Date | undefined

const initForm = () => {
  const inc = activeIncident.value
  if (!inc) return
  form.userSubmittedIncidentDescription = inc.userSubmittedIncidentDescription ?? ''
  form.incidentDescription = inc.incidentDescription ?? ''
  form.incidentDateTime = inc.incidentDateTime
  savedDateTime = inc.incidentDateTime
  form.address = inc.address ?? undefined
  form.hasContentWarning = inc.hasContentWarning ?? false
  form.isFilmedInPublicLocation = inc.isFilmedInPublicLocation ?? false
  form.userConsentProvided = inc.userConsentProvided ?? false
  form.location = inc.location
    ? {
        ...inc.location,
        coordinates: inc.location.coordinates ? [...inc.location.coordinates] : undefined
      }
    : undefined
  mapCenter.value = [
    inc.location?.coordinates?.[1] ?? -27.4698,
    inc.location?.coordinates?.[0] ?? 153.0251
  ]
}

// --- Media state ---
type ActiveMedia = { type: 'video' | 'image'; sourceId: string; response: ViewIncidentFileResponse }

const footageUrls = reactive<Record<string, ViewIncidentFileResponse>>({})
const imageUrls = reactive<Record<string, ViewIncidentFileResponse>>({})
const loadingUrls = ref(true)
const downloadTarget = ref<{ id: string; name: string } | null>(null)
const downloadOpen = computed({
  get: () => downloadTarget.value !== null,
  set: (open) => {
    if (!open) downloadTarget.value = null
  }
})
const activeMedia = ref<ActiveMedia | null>(null)
const deletedIds = ref<string[]>([])
const viewedIds = ref<string[]>([])
let viewTimer: ReturnType<typeof setTimeout> | null = null
const VIEWED_DELAY_MS = 3000

const visibleFootage = computed(() =>
  ((activeIncident.value?.userIncidentFootageCollection ?? []) as UserIncidentFootage[]).filter(
    (f) => !deletedIds.value.includes(f.id!)
  )
)
const visibleImages = computed(() =>
  ((activeIncident.value?.userIncidentImageCollection ?? []) as UserIncidentImage[]).filter(
    (f) => !deletedIds.value.includes(f.id!)
  )
)

const footageStatusMap = computed(() =>
  Object.fromEntries(
    ((activeIncident.value?.userIncidentFootageCollection ?? []) as UserIncidentFootage[]).map(
      (f) => [f.id!, f.mediaProcessingStatus]
    )
  )
)
const imageStatusMap = computed(() =>
  Object.fromEntries(
    ((activeIncident.value?.userIncidentImageCollection ?? []) as UserIncidentImage[]).map((f) => [
      f.id!,
      f.mediaProcessingStatus
    ])
  )
)

const activeMediaStatus = computed(() => {
  if (!activeMedia.value) return null
  const map = activeMedia.value.type === 'video' ? footageStatusMap.value : imageStatusMap.value
  return map[activeMedia.value.sourceId] ?? null
})

const activeMediaReady = computed(
  () =>
    activeMediaStatus.value === MediaProcessingStatus.Complete &&
    !!activeMedia.value?.response.convertedObjectUrl
)

// --- Lifecycle ---
// The component is always mounted in ModerationDashboard (not conditionally rendered),
// so onMounted fires when the page loads with no active incident yet. We can't use
// onMounted for per-incident init — watch the incident ID instead.
onMounted(() => {
  beginObserving(form)
})

onUnmounted(() => {
  cleanupObserver()
  if (viewTimer) clearTimeout(viewTimer)
  stopProcessingPoll()
})

// --- Processing detail polling ---
// Staff-only endpoint, so it is cheap to poll for as long as an incident is open. That also picks up a
// file that is reprocessed from the card, without tracking when it leaves a final state.
// Staff's choice for a pending crash link (RF-070): applied with the next Save or Approve & Save.
const crashDecision = ref<CrashLinkDecision>('keep')

const PROCESSING_POLL_MS = 5000
let processingTimer: ReturnType<typeof setInterval> | null = null

function stopProcessingPoll() {
  if (processingTimer) clearInterval(processingTimer)
  processingTimer = null
}

watch(
  () => [isDialogOpen.value, activeIncident.value?.id] as const,
  ([open, id]) => {
    stopProcessingPoll()
    if (!open || !id) return
    void moderationStore.fetchProcessing(id)
    processingTimer = setInterval(
      () => void moderationStore.fetchProcessing(id),
      PROCESSING_POLL_MS
    )
  },
  { immediate: true }
)

watch(
  () => activeIncident.value?.id,
  async (newId) => {
    if (!newId) return
    // Reset all per-incident local state
    activeMedia.value = null
    deletedIds.value = []
    viewedIds.value = []
    crashDecision.value = 'keep'
    void moderationStore.fetchLinkedCrash(newId, activeIncident.value?.openDataCrashId)
    if (viewTimer) {
      clearTimeout(viewTimer)
      viewTimer = null
    }
    Object.keys(footageUrls).forEach((k) => delete footageUrls[k])
    Object.keys(imageUrls).forEach((k) => delete imageUrls[k])
    // Reinitialise form and media for the new incident
    initForm()
    cleanupObserver()
    beginObserving(form)
    await fetchAllUrls()
  }
)

// --- URL fetching ---
const fetchAllUrls = async () => {
  loadingUrls.value = true
  const footage = (activeIncident.value?.userIncidentFootageCollection ??
    []) as UserIncidentFootage[]
  const images = (activeIncident.value?.userIncidentImageCollection ?? []) as UserIncidentImage[]

  Object.keys(footageUrls).forEach((k) => delete footageUrls[k])
  Object.keys(imageUrls).forEach((k) => delete imageUrls[k])

  try {
    await Promise.all([
      ...footage.map(async (f) => {
        try {
          footageUrls[f.id!] = await moderationStore.getFootageForDownload(f.id!)
        } catch {
          // leave absent; card shows placeholder
        }
      }),
      ...images.map(async (img) => {
        try {
          imageUrls[img.id!] = await moderationStore.getImageForDownload(img.id!)
        } catch {
          // leave absent; card shows placeholder
        }
      })
    ])

    if (footage.length > 0 && footageUrls[footage[0].id!]) {
      activeMedia.value = {
        type: 'video',
        sourceId: footage[0].id!,
        response: footageUrls[footage[0].id!]
      }
    } else if (images.length > 0 && imageUrls[images[0].id!]) {
      activeMedia.value = {
        type: 'image',
        sourceId: images[0].id!,
        response: imageUrls[images[0].id!]
      }
    }
  } finally {
    loadingUrls.value = false
  }
}

// --- Media selection ---
const selectMedia = (type: 'video' | 'image', id: string) => {
  const urls = type === 'video' ? footageUrls : imageUrls
  activeMedia.value = { type, sourceId: id, response: urls[id] ?? {} }
}

// --- Viewed tracking ---
watch(activeMedia, (val) => {
  if (viewTimer) {
    clearTimeout(viewTimer)
    viewTimer = null
  }
  if (val && !viewedIds.value.includes(val.sourceId)) {
    viewTimer = setTimeout(() => {
      if (activeMedia.value?.sourceId === val.sourceId) {
        viewedIds.value.push(val.sourceId)
      }
    }, VIEWED_DELAY_MS)
  }
})

// --- File navigation ---
const allFiles = computed(() => [
  ...visibleFootage.value.map((f) => ({ type: 'video' as const, id: f.id! })),
  ...visibleImages.value.map((f) => ({ type: 'image' as const, id: f.id! }))
])

const activeFileIndex = computed(() =>
  allFiles.value.findIndex((f) => f.id === activeMedia.value?.sourceId)
)

const goToPrev = () => {
  if (activeFileIndex.value <= 0) return
  const { type, id } = allFiles.value[activeFileIndex.value - 1]
  selectMedia(type, id)
}

const goToNext = () => {
  if (activeFileIndex.value < 0 || activeFileIndex.value >= allFiles.value.length - 1) return
  const { type, id } = allFiles.value[activeFileIndex.value + 1]
  selectMedia(type, id)
}

// --- Deletion ---
const removeFootage = (file: UserIncidentFootage) => {
  $q.dialog({
    title: 'Remove Footage',
    message: `Remove "${file.uploadFileName ?? 'this file'}" from the incident? This cannot be undone.`,
    cancel: true,
    ok: { label: 'Remove', color: 'negative', flat: true }
  }).onOk(async () => {
    deletedIds.value.push(file.id!)
    delete footageUrls[file.id!]
    if (activeMedia.value?.sourceId === file.id!) {
      const next = visibleFootage.value.find((f) => f.id !== file.id! && footageUrls[f.id!])
      if (next) {
        activeMedia.value = { type: 'video', sourceId: next.id!, response: footageUrls[next.id!] }
      } else if (visibleImages.value.length > 0) {
        const img = visibleImages.value[0]
        activeMedia.value = imageUrls[img.id!]
          ? { type: 'image', sourceId: img.id!, response: imageUrls[img.id!] }
          : null
      } else {
        activeMedia.value = null
      }
    }
    try {
      await moderationStore.deleteFootage(file.id!)
      $q.notify({ type: 'positive', message: 'Footage removed.' })
    } catch {
      const idx = deletedIds.value.indexOf(file.id!)
      if (idx > -1) deletedIds.value.splice(idx, 1)
      try {
        footageUrls[file.id!] = await moderationStore.getFootageForDownload(file.id!)
      } catch {
        // Best-effort restore of the footage URL; the failure notice below covers the user
      }
      $q.notify({ type: 'negative', message: 'Failed to remove footage.' })
    }
  })
}

const removeImage = (file: UserIncidentImage | UserIncidentFootage) => {
  $q.dialog({
    title: 'Remove Image',
    message: `Remove "${file.uploadFileName ?? 'this file'}" from the incident? This cannot be undone.`,
    cancel: true,
    ok: { label: 'Remove', color: 'negative', flat: true }
  }).onOk(async () => {
    deletedIds.value.push(file.id!)
    delete imageUrls[file.id!]
    if (activeMedia.value?.sourceId === file.id!) {
      const img = visibleImages.value.find((f) => f.id !== file.id!)
      if (img && imageUrls[img.id!]) {
        activeMedia.value = { type: 'image', sourceId: img.id!, response: imageUrls[img.id!] }
      } else if (visibleFootage.value.length > 0) {
        const vid = visibleFootage.value[0]
        activeMedia.value = footageUrls[vid.id!]
          ? { type: 'video', sourceId: vid.id!, response: footageUrls[vid.id!] }
          : null
      } else {
        activeMedia.value = null
      }
    }
    try {
      await moderationStore.deleteImage(file.id!)
      $q.notify({ type: 'positive', message: 'Image removed.' })
    } catch {
      const idx = deletedIds.value.indexOf(file.id!)
      if (idx > -1) deletedIds.value.splice(idx, 1)
      try {
        imageUrls[file.id!] = await moderationStore.getImageForDownload(file.id!)
      } catch {
        // Best-effort restore of the image URL; the failure notice below covers the user
      }
      $q.notify({ type: 'negative', message: 'Failed to remove image.' })
    }
  })
}

// --- Reprocessing files ---
const reprocessFile = async (file: UserIncidentImage | UserIncidentFootage) => {
  try {
    await moderationStore.reprocessUserFile(file.id!)
    $q.notify({ type: 'positive', message: 'Reprocessing triggered.' })
  } catch {
    $q.notify({ type: 'negative', message: 'Failed to trigger reprocessing.' })
  }
}

// --- Location ---
const lat = computed({
  get: () => (form.location?.coordinates?.[1] as number) ?? 0,
  set: (val: number) => {
    if (form.location?.coordinates) form.location.coordinates[1] = val
  }
})

const lng = computed({
  get: () => (form.location?.coordinates?.[0] as number) ?? 0,
  set: (val: number) => {
    if (form.location?.coordinates) form.location.coordinates[0] = val
  }
})

const onMarkerMove = (event: any) => {
  const { lat: newLat, lng: newLng } = event.target.getLatLng()
  if (form.location?.coordinates) {
    form.location.coordinates[0] = newLng
    form.location.coordinates[1] = newLat
  }
}

const onAddressUpdate = (val: AddressDto | null) => {
  form.address = val ? { ...val, rawInput: form.address?.rawInput } : undefined
}

const onAddressLocationSelected = (coords: { lat: number; lon: number }) => {
  nextTick(() => {
    $q.dialog({
      title: 'Move Pin?',
      message: 'Move the map pin to the selected address?',
      cancel: true,
      persistent: false
    }).onOk(() => {
      if (form.location?.coordinates) {
        form.location.coordinates[0] = coords.lon
        form.location.coordinates[1] = coords.lat
      }
      mapCenter.value = [coords.lat, coords.lon]
    })
  })
}

// --- Save / Approve / Reject ---
const saving = ref(false)
const rejecting = ref(false)

// After a successful PATCH the dialog stays open, so diff the next edit against what the server now has.
const rebaseline = () => {
  savedDateTime = form.incidentDateTime
  cleanupObserver()
  beginObserving(form)
}

const buildPatch = (
  extraFields: Partial<IncidentReport> = {},
  extraOps: Operation[] = []
): Operation[] => {
  let ops = dropUnchangedIncidentDateTime(generatePatch(), savedDateTime, form.incidentDateTime)

  // fast-json-patch generates /location/coordinates/0 and /location/coordinates/1
  // when lat/lng inputs change. NTS Point.Coordinates is a fixed-size array that
  // SystemTextJsonPatch cannot element-patch. Collapse to a single /location replace
  // so the server can deserialize the whole Point via GeoJsonConverterFactory.
  if (ops.some((op) => op.path.startsWith('/location')) && form.location) {
    ops = ops.filter((op) => !op.path.startsWith('/location'))
    ops.push({ op: 'replace', path: '/location', value: form.location })
  }

  for (const [key, value] of Object.entries(extraFields)) {
    ops.push({ op: 'replace', path: `/${key}`, value })
  }
  // After the /location collapse above: crash link operations never touch /location.
  return [...ops, ...extraOps]
}

// The queue row is not reloaded after a Save, so show the link as the server now has it.
const applySavedCrashLink = (ops: Operation[]) => {
  const incident = activeIncident.value
  const decided = ops.find((op) => op.path === '/crashLinkStatus')
  if (!incident || !decided || decided.op !== 'replace') return
  incident.crashLinkStatus = decided.value
  incident.crashLinkReviewedAt = new Date()
  crashDecision.value = 'keep'
}

// A 409 means the uploader changed the crash link after this dialog opened: show the server's text, reload what staff are
// looking at and drop the stale choice. Anything else is a plain failure.
const reportSaveFailure = async (e: unknown, fallback: string) => {
  if (!(e instanceof ResponseError) || e.response.status !== 409) {
    $q.notify({ type: 'negative', message: fallback })
    return
  }
  $q.notify({ type: 'warning', message: await readApiErrorMessage(e, fallback) })
  crashDecision.value = 'keep'
  await moderationStore.reloadActiveIncident()
}

const saveAndApprove = async () => {
  const id = activeIncident.value?.id
  if (!id) return
  saving.value = true
  const idx = activeIndex.value
  try {
    const linkOps = crashLinkOps(activeIncident.value, crashDecision.value, true)
    await moderationStore.patchIncident(
      id,
      buildPatch({ moderationStatus: ModerationStatus.Approved }, linkOps)
    )
    applySavedCrashLink(linkOps)
    rebaseline()
    $q.notify({ type: 'positive', message: 'Incident approved and live.' })
    await moderationStore.navigateNextAfterAction(idx)
  } catch (e) {
    await reportSaveFailure(e, 'Failed to approve incident.')
  } finally {
    saving.value = false
  }
}

const triggerSave = () => {
  const id = activeIncident.value?.id
  if (!id) return
  $q.dialog({
    title: 'Save Changes',
    message: 'Save changes to this incident without approving it?',
    cancel: true,
    ok: { label: 'Save', color: 'primary' }
  }).onOk(async () => {
    try {
      const linkOps = crashLinkOps(activeIncident.value, crashDecision.value, false)
      await moderationStore.patchIncident(id, buildPatch({}, linkOps))
      applySavedCrashLink(linkOps)
      rebaseline()
      $q.notify({ type: 'positive', message: 'Changes saved.' })
    } catch (e) {
      await reportSaveFailure(e, 'Failed to save changes.')
    }
  })
}

const triggerReject = () => {
  const id = activeIncident.value?.id
  if (!id) return
  $q.dialog({
    title: 'Reject Incident',
    message: 'Reject this incident? Any unsaved changes will be lost.',
    cancel: true,
    ok: { label: 'Reject', color: 'negative' }
  }).onOk(async () => {
    rejecting.value = true
    const idx = activeIndex.value
    try {
      const ops: Operation[] = [
        { op: 'replace', path: '/moderationStatus', value: ModerationStatus.Rejected }
      ]
      await moderationStore.patchIncident(id, ops)
      $q.notify({ type: 'info', message: 'Incident rejected.' })
      await moderationStore.navigateNextAfterAction(idx)
    } catch {
      $q.notify({ type: 'negative', message: 'Failed to reject incident.' })
    } finally {
      rejecting.value = false
    }
  })
}

const queuePosition = computed(() => ({
  current: activeIndex.value >= 0 ? activeIndex.value + 1 : '?',
  total: queueTotal.value
}))
</script>

<template>
  <q-dialog v-model="isOpen" maximized transition-show="slide-up" transition-hide="slide-down">
    <q-card class="column no-wrap full-height bg-grey-1">
      <!-- Toolbar -->
      <q-toolbar class="bg-primary text-white">
        <q-btn flat round icon="mdi-close" v-close-popup>
          <q-tooltip>Close</q-tooltip>
        </q-btn>
        <q-toolbar-title>
          Review Incident
          <span class="text-body2 q-ml-sm opacity-70">
            {{ queuePosition.current }} / {{ queuePosition.total }} in queue
          </span>
        </q-toolbar-title>
        <q-btn
          flat
          round
          icon="mdi-chevron-left"
          :disable="activeIndex <= 0"
          @click="moderationStore.openIncident(queueItems[activeIndex - 1]?.id)"
        >
          <q-tooltip>Previous incident</q-tooltip>
        </q-btn>
        <q-btn
          flat
          round
          icon="mdi-chevron-right"
          :disable="activeIndex < 0 || activeIndex >= queueTotal - 1"
          @click="moderationStore.openIncident(queueItems[activeIndex + 1]?.id)"
        >
          <q-tooltip>Next incident</q-tooltip>
        </q-btn>
      </q-toolbar>

      <!-- Body -->
      <q-scroll-area class="col" v-if="activeIncident">
        <div :key="activeIncident.id" class="row q-col-gutter-md q-pa-lg">
          <!-- Left: Preview + File Lists -->
          <div class="col-12 col-md-5">
            <div class="sticky-top">
              <!-- Main Preview -->
              <q-card flat bordered class="bg-black overflow-hidden">
                <div v-if="loadingUrls" class="flex flex-center" style="min-height: 220px">
                  <q-spinner color="white" size="xl" />
                </div>
                <div
                  v-else-if="!activeMedia"
                  class="flex flex-center text-grey-6"
                  style="min-height: 220px"
                >
                  <div class="text-center">
                    <q-icon name="mdi-play-circle-outline" size="5rem" />
                    <div class="text-body2 q-mt-sm">No media selected</div>
                  </div>
                </div>
                <template v-else>
                  <q-responsive
                    v-if="!activeMediaReady"
                    :ratio="16 / 9"
                    class="bg-grey-9 full-width"
                  >
                    <div class="flex flex-center column text-grey-5 q-gutter-y-sm">
                      <q-spinner
                        v-if="activeMediaStatus === MediaProcessingStatus.Processing"
                        size="lg"
                        color="grey-5"
                      />
                      <q-icon
                        v-else-if="activeMediaStatus === MediaProcessingStatus.Failed"
                        name="mdi-alert-circle-outline"
                        size="lg"
                        color="negative"
                      />
                      <q-icon v-else name="mdi-clock-outline" size="lg" />
                      <div class="text-caption">
                        {{
                          activeMediaStatus === MediaProcessingStatus.Failed
                            ? 'Processing Failed'
                            : 'Media Pending'
                        }}
                      </div>
                    </div>
                  </q-responsive>
                  <template v-else>
                    <q-responsive
                      v-if="activeMedia.type === 'video'"
                      :ratio="16 / 9"
                      class="full-width"
                    >
                      <QMediaPlayer
                        :key="activeMedia.response.convertedObjectUrl"
                        :source="activeMedia.response.convertedObjectUrl || ''"
                        type="video"
                        class="full-width"
                        :controls-display-time="3000"
                        dense
                        hide-volume-slider
                        hide-settings-btn
                      />
                    </q-responsive>
                    <q-img
                      v-else
                      :src="activeMedia.response.convertedObjectUrl || ''"
                      class="full-width"
                      fit="contain"
                      style="max-height: 60vh; min-height: 200px"
                    >
                      <template #error>
                        <q-responsive :ratio="16 / 9" class="full-width">
                          <div class="flex flex-center column text-grey-5 q-gutter-y-sm">
                            <q-icon name="mdi-image-broken" size="lg" />
                            <div class="text-caption">Media Unavailable</div>
                          </div>
                        </q-responsive>
                      </template>
                    </q-img>
                  </template>
                </template>
              </q-card>

              <!-- Prev / Next file navigation -->
              <div v-if="allFiles.length > 1" class="row items-center justify-between q-mt-sm">
                <q-btn
                  flat
                  dense
                  round
                  icon="mdi-chevron-left"
                  :disable="activeFileIndex <= 0"
                  @click="goToPrev"
                />
                <span class="text-caption text-grey-6">
                  {{ activeFileIndex >= 0 ? activeFileIndex + 1 : '—' }} / {{ allFiles.length }}
                </span>
                <q-btn
                  flat
                  dense
                  round
                  icon="mdi-chevron-right"
                  :disable="activeFileIndex < 0 || activeFileIndex >= allFiles.length - 1"
                  @click="goToNext"
                />
              </div>

              <!-- Footage grid -->
              <template v-if="visibleFootage.length">
                <div
                  class="text-caption text-grey-6 text-uppercase q-mt-md q-mb-xs q-gutter-x-xs row items-center"
                >
                  <q-icon name="mdi-video" size="xs" />
                  <span>Footage ({{ visibleFootage.length }})</span>
                </div>
                <div class="row wrap q-gutter-sm">
                  <MediaFileCard
                    v-for="file in visibleFootage"
                    :key="file.id"
                    :file="file"
                    :url-data="footageUrls[file.id!]"
                    :media-status="footageStatusMap[file.id!]"
                    :processing="processingByFileId[file.id!]"
                    :is-selected="activeMedia?.sourceId === file.id"
                    :is-viewed="viewedIds.includes(file.id!)"
                    :is-primary="activeIncident?.primaryUserIncidentFileId === file.id"
                    :is-loading="loadingUrls"
                    media-type="video"
                    @select="selectMedia('video', file.id!)"
                    @delete="removeFootage(file)"
                    @download="
                      downloadTarget = { id: file.id!, name: file.uploadFileName ?? 'file' }
                    "
                    @reprocess="reprocessFile(file)"
                    @set-primary="moderationStore.setPrimaryFile(activeIncident!.id!, file.id!)"
                  />
                </div>
              </template>

              <!-- Images grid -->
              <template v-if="visibleImages.length">
                <div
                  class="text-caption text-grey-6 text-uppercase q-mt-sm q-mb-xs q-gutter-x-xs row items-center"
                >
                  <q-icon name="mdi-image" size="xs" />
                  <span>Images ({{ visibleImages.length }})</span>
                </div>
                <div class="row wrap q-gutter-sm">
                  <MediaFileCard
                    v-for="file in visibleImages"
                    :key="file.id"
                    :file="file"
                    :url-data="imageUrls[file.id!]"
                    :media-status="imageStatusMap[file.id!]"
                    :processing="processingByFileId[file.id!]"
                    :is-selected="activeMedia?.sourceId === file.id"
                    :is-viewed="viewedIds.includes(file.id!)"
                    :is-primary="activeIncident?.primaryUserIncidentFileId === file.id"
                    :is-loading="loadingUrls"
                    media-type="image"
                    @select="selectMedia('image', file.id!)"
                    @delete="removeImage(file)"
                    @download="
                      downloadTarget = { id: file.id!, name: file.uploadFileName ?? 'file' }
                    "
                    @reprocess="reprocessFile(file)"
                    @set-primary="moderationStore.setPrimaryFile(activeIncident!.id!, file.id!)"
                  />
                </div>
              </template>

              <div
                v-if="!loadingUrls && !visibleFootage.length && !visibleImages.length"
                class="text-caption text-grey-5 text-center q-mt-md"
              >
                No media files attached to this incident.
              </div>
            </div>
          </div>

          <!-- Right: Form -->
          <div class="col-12 col-md-7">
            <div class="q-gutter-y-sm">
              <div>
                <q-input
                  v-model="form.userSubmittedIncidentDescription"
                  label="User-submitted description"
                  outlined
                  dense
                  readonly
                  type="textarea"
                  rows="10"
                />
                <div class="row justify-end q-mt-xs">
                  <q-btn
                    flat
                    dense
                    size="sm"
                    icon="mdi-arrow-down"
                    label="Copy to description"
                    color="primary"
                    @click="form.incidentDescription = form.userSubmittedIncidentDescription ?? ''"
                  />
                </div>
              </div>
              <q-input
                v-model="form.incidentDescription"
                label="Description"
                outlined
                dense
                type="textarea"
                rows="10"
              />
              <div>
                <div class="row q-col-gutter-sm">
                  <div class="col-6">
                    <date-time-picker
                      :model-value="form.incidentDateTime ?? new Date()"
                      @update:model-value="form.incidentDateTime = $event"
                      label="Timestamp"
                      outlined
                      dense
                    />
                  </div>
                  <q-input v-model.number="lat" label="Lat" outlined dense class="col-3" />
                  <q-input v-model.number="lng" label="Long" outlined dense class="col-3" />
                </div>
              </div>
              <q-input
                :model-value="form.address?.rawInput || ''"
                label="User-submitted address"
                outlined
                dense
                readonly
              />
              <address-autocomplete
                :model-value="form.address"
                @update:model-value="onAddressUpdate"
                @location-selected="onAddressLocationSelected"
              />

              <div class="map-container rounded-borders shadow-1 overflow-hidden q-mt-sm">
                <l-map
                  :zoom="16"
                  :center="mapCenter"
                  :max-bounds="maxBounds"
                  :use-global-leaflet="false"
                >
                  <l-tile-layer
                    url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                  />
                  <l-marker
                    :lat-lng="[lat, lng]"
                    :icon="markerIcon"
                    draggable
                    @moveend="onMarkerMove"
                  />
                </l-map>
              </div>

              <CrashLinkPanel
                v-if="activeIncident?.openDataCrashId"
                v-model:decision="crashDecision"
                :crash="linkedCrash"
                :status="activeIncident.crashLinkStatus"
                :reviewed-at="activeIncident.crashLinkReviewedAt"
              />

              <div class="row q-gutter-x-lg q-pt-sm">
                <q-toggle v-model="form.hasContentWarning" label="Content Warning" />
                <q-toggle v-model="form.isFilmedInPublicLocation" label="Filmed in Public" />
                <q-toggle v-model="form.userConsentProvided" label="Consent Provided" />
              </div>

              <div class="row justify-between q-pt-md">
                <q-btn
                  label="Reject"
                  color="negative"
                  flat
                  icon="mdi-close-circle"
                  :loading="rejecting"
                  @click="triggerReject"
                />
                <div class="row q-gutter-x-sm">
                  <q-btn
                    label="Save"
                    color="primary"
                    outline
                    icon="mdi-content-save"
                    @click="triggerSave"
                  />
                  <q-btn
                    label="Approve & Save"
                    color="primary"
                    unelevated
                    icon="mdi-check-circle"
                    :loading="saving"
                    @click="saveAndApprove"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </q-scroll-area>

      <div v-else class="col flex flex-center text-grey-6">
        <q-spinner size="xl" />
      </div>

      <MediaDownloadDialog
        v-model="downloadOpen"
        :user-file-id="downloadTarget?.id ?? null"
        :file-name="downloadTarget?.name ?? ''"
      />
    </q-card>
  </q-dialog>
</template>

<style lang="scss" scoped>
.sticky-top {
  position: sticky;
  top: 20px;
}

.map-container {
  height: 250px;
  width: 100%;
}
</style>
